import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { formatDuration, minutesToLabel } from './use-booking-form';

interface GhostSlotProps {
  startMinutes: number;
  durationMin: number;
  customerName: string;
  staffLabel: string;
}

interface GhostRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const rowId = (minutes: number) =>
  `to_${moment().startOf('day').add(minutes, 'minute').format('hh-mm-A')}`;

// Pending-slot ghost over the real CalendarUI's ANYONE column while a time is
// selected on the booking form. Anchors on the calendar table's `to_hh-mm-A`
// row ids (same technique as CalendarRealTimeLine); renders nothing when the
// anchors are absent.
const GhostSlot = ({ startMinutes, durationMin, customerName, staffLabel }: GhostSlotProps) => {
  const [rect, setRect] = useState<GhostRect | null>(null);

  useEffect(() => {
    const measure = () => {
      const startRow = document.getElementById(rowId(startMinutes));
      // rowSpan'd appointment cells can shift positional indexes, so only the
      // explicit data-anyone marker is trustworthy for the ANYONE column
      const anyoneCell = startRow?.querySelector<HTMLTableCellElement>('td[data-anyone]');
      if (!startRow || !anyoneCell) return setRect(null);
      const startBox = startRow.getBoundingClientRect();
      const cellBox = anyoneCell.getBoundingClientRect();
      const endRow = document.getElementById(rowId(startMinutes + durationMin));
      const height = endRow
        ? endRow.getBoundingClientRect().top - startBox.top
        : durationMin * 4; // calendar scale fallback: 4px per minute
      setRect({ top: startBox.top, left: cellBox.left + 4, width: cellBox.width - 8, height });
    };
    measure();
    // capture-phase scroll catches the calendar's inner scroll container
    document.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      document.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [startMinutes, durationMin]);

  if (!rect) return null;
  return (
    <div className="cid-ghost" style={rect}>
      <div className="cid-ghost-time">{minutesToLabel(startMinutes)} · {formatDuration(durationMin)}</div>
      <div className="cid-ghost-name">{customerName} · {staffLabel}</div>
    </div>
  );
};

export default GhostSlot;
